import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  DecorativeElement,
  GradientText,
  FloatingElement,
} from "../../components/Decorative";
import {
  FaUtensils,
  FaBookOpen,
  FaPhoneAlt,
  FaConciergeBell,
  FaUsers,
  FaTrophy,
  FaMapMarkerAlt,
  FaEnvelope,
  FaClock,
} from "react-icons/fa";
import biriyani from "../../img/biriyani.webp";
import bread from "../../img/bread.webp";
import kebab from "../../img/kebab.webp";

const HomePreviewSections: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const storyImages = [
    biriyani, // Hyderabadi biryani
    bread, // Naan bread
    kebab, // Chicken kebab
  ];

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-16 space-y-32">
      {/* About Preview - Enhanced */}
      <motion.section
        className="relative"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <DecorativeElement type="corners" />
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeInUp} className="space-y-6">
            <div className="inline-block">
              <motion.div
                className="flex items-center gap-2 bg-gradient-to-r from-[#ac9430]/10 to-[#ac9430]/10 px-4 py-2 rounded-full mb-4"
                whileHover={{ scale: 1.05 }}
              >
                <FaTrophy className="text-[#ac9430]" />
                <span className="text-sm font-semibold text-[#ac9430]">
                  Award Winning Restaurant
                </span>
              </motion.div>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-headingColor">
              Our Story
            </h2>

            <div className="space-y-4 text-textColor leading-relaxed">
              <p className="text-lg">
                Experience the authentic flavors of India at Royal Curry Leaf.
                Our culinary journey began with a passion for bringing
                traditional recipes to life with a modern twist.
              </p>
              <p>
                With over 20 years of expertise, our chefs craft each dish with
                love, using only the finest ingredients and time-honored
                techniques passed down through generations.
              </p>
            </div>

            <div className="flex gap-8 pt-4">
              <div className="text-center">
                <motion.p
                  className="text-3xl font-bold text-[#ac9430]"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  20+
                </motion.p>
                <p className="text-sm text-textColor">Years Experience</p>
              </div>
              <div className="text-center">
                <motion.p
                  className="text-3xl font-bold text-[#ac9430]"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  100+
                </motion.p>
                <p className="text-sm text-textColor">Dishes</p>
              </div>
              <div className="text-center">
                <motion.p
                  className="text-3xl font-bold text-[#ac9430]"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  5000+
                </motion.p>
                <p className="text-sm text-textColor">Happy Customers</p>
              </div>
            </div>

            <Link to="/about" className="inline-block">
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 10px 30px rgba(172, 148, 48, 0.3)",
                }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-[#ac9430] to-[#227a32] text-white px-8 py-3 rounded-full font-semibold shadow-lg transition-all duration-300"
              >
                Discover Our Story
              </motion.button>
            </Link>
          </motion.div>

          <FloatingElement className="relative" delay={0.2}>
            <div className="grid grid-cols-2 gap-4">
              {storyImages.map((img, idx) => (
                <motion.div
                  key={idx}
                  className={`rounded-2xl overflow-hidden shadow-xl ${
                    idx === 0 ? "col-span-2" : ""
                  }`}
                  whileHover={{ scale: 1.05, rotate: idx % 2 === 0 ? 2 : -2 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={img}
                    alt={`Story ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              ))}
            </div>
          </FloatingElement>
        </div>
      </motion.section>

      {/* Services Preview - Enhanced */}
      <motion.section
        className="relative py-16 px-8 bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-2xl"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <DecorativeElement type="corners" />

        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            className="inline-flex items-center gap-2 bg-[#ac9430]/10 px-4 py-2 rounded-full mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <FaConciergeBell className="text-[#ac9430]" />
            <span className="text-sm font-semibold text-[#ac9430]">
              What We Offer
            </span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-headingColor">
            Our Services
          </h2>
          <p className="text-lg text-textColor">
            Whether it’s premium delivery, virtual pop-ups, or catering for
            events, our ghost kitchen provides flexible services that leave a
            lasting impression.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: <FaUtensils className="text-4xl" />,
              title: "Premium delivery",
              description:
                "Enjoy our gourmet meals delivered straight to your door. Fine-dining quality made for delivery.",
              color: "from-[#ac9430] to-[#227a32]",
            },
            {
              icon: <FaBookOpen className="text-4xl" />,
              title: "Event Catering",
              description:
                "Let us cater your special occasions with excellence and authentic flavors",
              color: "from-[#227a32] to-[#ac9430]",
            },
            {
              icon: <FaUsers className="text-4xl" />,
              title: "Private Events",
              description:
                "Host your celebrations in our elegant private dining spaces",
              color: "from-[#ac9430] to-[#227a32]",
            },
          ].map((service, index) => (
            <motion.div
              key={index}
              {...fadeInUp}
              transition={{ delay: index * 0.2 }}
              className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
              whileHover={{ y: -10 }}
            >
              {/* Gradient background on hover */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
              />

              {/* Icon with gradient */}
              <motion.div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} text-white mb-6 shadow-lg`}
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                {service.icon}
              </motion.div>

              <h3 className="text-2xl font-bold text-headingColor mb-4 group-hover:text-[#ac9430] transition-colors">
                {service.title}
              </h3>
              <p className="text-textColor leading-relaxed">
                {service.description}
              </p>

              {/* Decorative element */}
              <motion.div
                className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${service.color}`}
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                transition={{ delay: index * 0.2 + 0.5, duration: 0.6 }}
              />
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/services" className="inline-block">
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 30px rgba(255, 90, 31, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-[#ac9430] to-[#227a32] text-white px-8 py-3 rounded-full font-semibold shadow-lg"
            >
              View All Services
            </motion.button>
          </Link>
        </div>
      </motion.section>

      {/* Contact Preview - Enhanced */}
      <motion.section
        className="relative"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <DecorativeElement type="corners" />
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeInUp} className="space-y-8">
            <div className="inline-block">
              <motion.div
                className="flex items-center gap-2 bg-[#ac9430]/10 px-4 py-2 rounded-full mb-4"
                whileHover={{ scale: 1.05 }}
              >
                <FaPhoneAlt className="text-[#ac9430]" />
                <span className="text-sm font-semibold text-[#ac9430]">
                  We're Here For You
                </span>
              </motion.div>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-headingColor">
              Get in Touch
            </h2>

            <p className="text-lg text-textColor leading-relaxed">
              Have questions or want to make a reservation? We're here to help
              make your dining experience perfect.
            </p>

            <div className="space-y-6">
              {[
                {
                  icon: <FaPhoneAlt className="text-[#ac9430]" />,
                  label: "Call Us",
                  value: "+1 (857) 605-5777",
                },
                {
                  icon: <FaEnvelope className="text-[#ac9430]" />,
                  label: "Email Us",
                  value: "royalcurryleaf@gmail.com",
                },
                {
                  icon: <FaMapMarkerAlt className="text-[#ac9430]" />,
                  label: "Visit Us",
                  value: "34 SW Park, Westwood, MA 02090",
                },
                {
                  icon: <FaClock className="text-[#ac9430]" />,
                  label: "Working Hours",
                  value: "Mon-Sun: 11:30 AM - 9:00 PM",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white shadow-md hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ x: 10 }}
                >
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-[#ac9430] shadow-lg group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-sm text-textColor font-medium">
                      {item.label}
                    </p>
                    <p className="text-headingColor font-semibold">
                      {item.value}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              onClick={() => window.location.href = 'tel:+18576055777'}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 30px rgba(255, 90, 31, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              className="inline-block cursor-pointer"
            >
              <div className="bg-gradient-to-r from-[#ac9430] to-[#227a32] text-white px-8 py-3 rounded-full font-semibold shadow-lg">
                Contact Us Now
              </div>
            </motion.div>
          </motion.div>

          <FloatingElement className="relative" delay={0.2}>
            <motion.div
              className="relative w-full h-96 bg-gradient-to-br from-[#ac9430] to-[#227a32] rounded-3xl shadow-2xl overflow-hidden p-8 flex flex-col justify-between"
              whileHover={{ scale: 1.05, boxShadow: "0 20px 50px rgba(172, 148, 48, 0.3)" }}
            >
              {/* Top decorative elements */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16" />
              
              {/* Content */}
              <div className="relative z-10 space-y-6">
                <div>
                  <motion.div
                    className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl backdrop-blur-sm mb-4"
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <FaUtensils className="text-3xl text-white" />
                  </motion.div>
                  <h3 className="text-3xl font-bold text-white mb-2">
                    Our Professional Kitchen
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed">
                    State-of-the-art culinary facilities where tradition meets innovation. Our expert chefs craft authentic Indian dishes with precision and passion every single day.
                  </p>
                </div>

                <div className="space-y-3 pt-4">
                  <div className="flex items-center gap-3 text-white/90">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    <span className="text-sm">Premium certified kitchen facilities</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/90">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    <span className="text-sm">Expert chefs with 20+ years experience</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/90">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    <span className="text-sm">Highest quality ingredients sourced fresh daily</span>
                  </div>
                </div>
              </div>

              {/* Bottom accent */}
              <motion.div
                className="relative z-10 inline-flex items-center gap-2 mt-6 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full w-fit"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-sm text-white font-semibold">Operating Daily 11:30 AM - 9:00 PM</span>
              </motion.div>
            </motion.div>
          </FloatingElement>
        </div>
      </motion.section>

      <DecorativeElement type="divider" className="my-16" />
    </div>
  );
};

export default HomePreviewSections;
